import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import Loader from '../../components/Loader';
import { toast } from 'react-toastify';
import ApiService from '../../services/ApiService';
import { formatDate } from '../../utils/helper';
import Swal from 'sweetalert2';

const Details = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [isLoader, setIsLoader] = useState(false);
  const [notification, setNotification] = useState(null);

  useEffect(() => {
    if (id) {
      getNotificationDetails();
    }
  }, [id]);

  const getNotificationDetails = async () => {
    setIsLoader(true);
    try {
      const response = await ApiService.request({
        method: 'GET',
        url: `custom-notifications/${id}`,
      });
      const data = response.data;
      if (data.status) {
        setNotification(data.data);
      } else {
        toast.error(data.message);
        navigate('/custom-notifications');
      }
    } catch (error) {
      console.error(error);
      toast.error('Failed to load notification details');
      navigate('/custom-notifications');
    } finally {
      setIsLoader(false);
    }
  };

  const delData = async () => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: 'you will not be able to revert it!',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      confirmButtonText: 'Yes, delete it!',
      cancelButtonColor: '#9E9B9B',
      cancelButtonText: 'No',
    });

    if (result.isConfirmed) {
      const response = await ApiService.request({
        method: 'DELETE',
        url: `custom-notifications/${id}`,
      });
      const data = response.data;
      if (data.status) {
        toast.success(data.message);
        navigate('/custom-notifications');
      } else {
        toast.error(data.message);
      }
    }
  };

  if (isLoader && !notification) {
    return <Loader text="Loading" size="30" className="text-center" />;
  }

  if (!notification) {
    return null;
  }

  return (
    <div className="d-flex flex-column flex-column-fluid">
      <div id="kt_app_toolbar" className="app-toolbar py-3 py-lg-6">
        <div id="kt_app_toolbar_container" className="app-container container-xxl d-flex flex-stack">
          <div className="page-title d-flex flex-column justify-content-center flex-wrap me-3">
            <h1 className="page-heading d-flex text-dark fw-bold fs-3 flex-column justify-content-center my-0">
              Notification Details
            </h1>
            <ul className="breadcrumb breadcrumb-separatorless fw-semibold fs-7 my-0 pt-1">
              <li className="breadcrumb-item text-muted">
                <Link to="/dashboard" className="text-muted text-hover-primary">Dashboard</Link>
              </li>
              <li className="breadcrumb-item">
                <span className="bullet bg-gray-400 w-5px h-2px"></span>
              </li>
              <li className="breadcrumb-item text-muted">
                <Link to="/custom-notifications" className="text-muted text-hover-primary">Custom Notifications</Link>
              </li>
              <li className="breadcrumb-item">
                <span className="bullet bg-gray-400 w-5px h-2px"></span>
              </li>
              <li className="breadcrumb-item text-muted">Details</li>
            </ul>
          </div>
          <div className="d-flex align-items-center gap-2 gap-lg-3">
            <button onClick={delData} className="btn btn-sm btn-light-danger">
              <i className="bi bi-trash-fill me-2"></i>Delete
            </button>
          </div>
        </div>
      </div>

      <div id="kt_app_content" className="app-content flex-column-fluid">
        <div id="kt_app_content_container" className="app-container container-xxl">
          <div className="card">
            <div className="card-body pt-0">
              <div className="row g-9 my-3">
                <div className="col-md-6">
                  <label className="fs-6 fw-semibold mb-2 text-muted">Title</label>
                  <div className="fs-6 fw-bold text-gray-800 mb-4">{notification.title}</div>
                </div>
                {notification.title_ar && (
                  <div className="col-md-6">
                    <label className="fs-6 fw-semibold mb-2 text-muted">Title Arabic</label>
                    <div className="fs-6 fw-bold text-gray-800 mb-4">{notification.title_ar}</div>
                  </div>
                )}
                
                <div className="col-md-6">
                  <label className="fs-6 fw-semibold mb-2 text-muted">Message</label>
                  <div className="fs-6 text-gray-800 mb-4">{notification.message}</div>
                </div>
                {notification.message_ar && (
                  <div className="col-md-6">
                    <label className="fs-6 fw-semibold mb-2 text-muted">Message Arabic</label>
                    <div className="fs-6 text-gray-800 mb-4">{notification.message_ar}</div>
                  </div>
                )}

                <div className="col-md-6">
                  <label className="fs-6 fw-semibold mb-2 text-muted">Recipient Type</label>
                  <div className="mb-4">
                    <span className={`badge ${notification.recipient_type === 'all' ? 'badge-light-primary' : 'badge-light-info'}`}>
                      {notification.recipient_type === 'all' ? 'All Users' : 'Specific Users'}
                    </span>
                  </div>
                </div>

                <div className="col-md-6">
                  <label className="fs-6 fw-semibold mb-2 text-muted">Total Sent</label>
                  <div className="mb-4">
                    <span className="badge badge-light-success fs-6">
                      {notification.total_sent || 0}
                    </span>
                  </div>
                </div>

                <div className="col-md-6">
                  <label className="fs-6 fw-semibold mb-2 text-muted">Total Failed</label>
                  <div className="mb-4">
                    <span className="badge badge-light-danger fs-6">
                      {notification.total_failed || 0}
                    </span>
                  </div>
                </div>

                <div className="col-md-6">
                  <label className="fs-6 fw-semibold mb-2 text-muted">Sent By</label>
                  <div className="fs-6 text-gray-800 mb-4">
                    {notification.sent_by?.name || 'N/A'} 
                    {notification.sent_by?.email && (
                      <span className="text-muted"> ({notification.sent_by.email})</span>
                    )}
                  </div>
                </div>

                <div className="col-md-6">
                  <label className="fs-6 fw-semibold mb-2 text-muted">Created Date</label>
                  <div className="fs-6 text-gray-800 mb-4">
                    {formatDate(notification.createdAt, 'DD MMM YYYY, hh:mm A')}
                  </div>
                </div>

                {notification.recipient_type === 'specific' && notification.recipient_ids && notification.recipient_ids.length > 0 && (
                  <div className="col-md-12">
                    <label className="fs-6 fw-semibold mb-2 text-muted">Recipients</label>
                    <div className="table-responsive">
                      <table className="table table-bordered table-hover">
                        <thead>
                          <tr>
                            <th>Name</th>
                            <th>Email</th>
                            <th>Phone</th>
                          </tr>
                        </thead>
                        <tbody>
                          {notification.recipient_ids.map((user, index) => (
                            <tr key={index}>
                              <td>{user.name || 'N/A'}</td>
                              <td>{user.email || 'N/A'}</td>
                              <td>{user.phone || 'N/A'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>

              <div className="row g-9 my-3">
                <div className="text-center">
                  <Link to="/custom-notifications" className="btn btn-light">
                    <i className="bi bi-arrow-left me-2"></i>Back to List
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Details;

